import { useRef, useState } from "react";
import { Button, Card, Col, Overlay, Row, Tooltip } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import localizedLink from "../../utils/localizedLink";
import productTitle from "../../utils/productTitle";
import Countdown from "../../components/Countdown";
import Price from "../../components/Price";
import ProductLabel from "../../components/ProductLabel";
import SolarCharging from "../../components/SolarCharging";
import useSunlight from "../../hooks/useSunlight";
import { isOutOfStock } from "../../contexts/Cart";
import { ShoppingCart } from "lucide-react";

// `solar` is for solar cameras: the sun lights the top left corner of the
// cards in the middle of the screen, shining on their panels, and charges the
// batteries there (see hooks/useSunlight).
export default function Item({ title, products, addToCart, solar = false }) {
  const { i18n, t } = useTranslation();
  const lang = i18n.language;
  const cards = useRef([]);
  const lit = useSunlight(cards, solar);
  const [showStates, setShowStates] = useState({});
  const [targetStates, setTargetStates] = useState({});
  const ref = useRef(null);

  const handleClick = (event, productId) => {
    // Read currentTarget now: React clears it once the handler returns, before
    // the state updater below runs.
    const button = event.currentTarget;
    setShowStates((prev) => ({
      ...prev,
      [productId]: true,
    }));
    setTargetStates((prev) => ({
      ...prev,
      [productId]: button,
    }));

    // Hide the tooltip after 3 seconds
    setTimeout(() => {
      setShowStates((prev) => ({
        ...prev,
        [productId]: false,
      }));
    }, 2000);
  };

  return (
    <Row className="mt-4 mt-sm-5">
      <h3 className={"mb-3 text-xl"}>{title}</h3>
      <hr />
      {products.map((product, index) => (
        <Col
          className="mt-3 mt-sm-4"
          xs={12}
          sm={6}
          lg={4}
          xl={3}
          key={product.id}
          ref={ref}
        >
          {/* Product images are transparent, so they sit on the gray card.
              Phones get a short horizontal row (image beside the details);
              from Bootstrap's sm breakpoint (576px) up, a vertical card. */}
          <Card
            ref={(card) => (cards.current[index] = card)}
            className={`eufy-card product-card h-full ${solar ? "sunny" : ""}`}
          >
            {product.oldPrice !== product.price &&
              product.discountTitleKey && (
                <Link to={localizedLink(`/${product.id}`, lang)}>
                  <div className="bg-[#080a0f] text-white text-center m-2 px-2 py-1 rounded-xl">
                    {t(product.discountTitleKey)}
                    <Countdown targetDate={new Date('2025-11-28T23:59:59')} />
                  </div>
                </Link>
              )}
            <div className="flex flex-1 min-[576px]:flex-col">
              {/* pt-6 leaves room above the product for the label in the top
                  right corner and the solar battery in the top left. On
                  phones the image is centered in a column taller than itself,
                  and cropped square: every product photo has at least 18%
                  transparent margin per side, so the crop only trims empty
                  space and draws the product bigger. */}
              <Link
                to={localizedLink(`/${product.id}`, lang)}
                className="relative flex w-[40%] shrink-0 items-center min-[576px]:block min-[576px]:w-auto min-[576px]:pt-6"
              >
                <Card.Img
                  variant="top"
                  loading="lazy"
                  src={product.imageUrl}
                  className="aspect-square object-cover min-[576px]:aspect-auto"
                />
                {/* On phones the battery is the card's padding, 16px, from
                    its top and left, as the title's letters are from the top
                    (see Card.Title below), and the label is centered in the
                    space under the product, as far from the bottom as the
                    buttons. Larger cards have them in their top corners. */}
                {solar && (
                  <SolarCharging
                    charging={Boolean(lit[index])}
                    className="absolute left-4 top-4 min-[576px]:left-3 min-[576px]:top-3"
                  />
                )}
                {product.label && (
                  <span className="absolute inset-x-3 bottom-4 flex justify-center min-[576px]:bottom-auto min-[576px]:top-3 min-[576px]:justify-end">
                    <ProductLabel label={product.label} />
                  </span>
                )}
              </Link>
              <Card.Body className="d-flex flex-column min-w-0">
                {/* On phones the title's line leaves 7px above its letters;
                    pulled up by that, they're as far from the card's top as
                    the buttons are from its bottom. */}
                <Card.Title className="-mt-[7px] min-[576px]:mt-0">
                  <Link to={localizedLink(`/${product.id}`, lang)}>
                    <p className="text-lg min-[576px]:text-xl">{productTitle(product, lang)}</p>
                  </Link>
                </Card.Title>
                <Price product={product} className="mb-2" />
                <div className="text-sm min-[576px]:text-base">
                  {product.description.map(({ key, value }) => (
                    <p key={key[lang]}>
                      <span className="text-[#626468]">{key[lang]}</span> {value[lang]}
                    </p>
                  ))}
                </div>
                {/* mt-auto keeps the buttons aligned across a row of cards. */}
                <div className="mt-auto flex pt-3">
                  <Link to={localizedLink(`/${product.id}`, lang)}>
                    <Button
                      variant="light"
                      className="eufy-button eufy-button-secondary whitespace-nowrap"
                    >
                      {t("home.details")}
                    </Button>
                  </Link>
                  <Button
                    id={`${product.id}-cart-button`}
                    disabled={isOutOfStock(product)}
                    variant="dark"
                    aria-label={t("product.add-to-cart")}
                    className="ml-3 eufy-button inline-flex items-center"
                    onClick={(e) => {
                      handleClick(e, product.id);
                      addToCart(product);
                    }}
                  >
                    <ShoppingCart className="h-[18px] w-[18px]" strokeWidth={2} />
                  </Button>
                </div>
                <Overlay
                  show={showStates[product.id] || false}
                  target={targetStates[product.id]}
                  placement="top"
                  container={ref}
                >
                  <Tooltip id={`tooltip-${product.id}`}>
                    {t("home.added-to-cart")}
                  </Tooltip>
                </Overlay>
              </Card.Body>
            </div>
          </Card>
        </Col>
      ))}
    </Row>
  );
}
